// src/pages/nascar/NascarRangeOfOutcomes.jsx
import React, { useEffect, useMemo, useState } from "react";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  ReferenceLine,
} from "recharts";
/* ------------------------------------------------------------------
   NASCAR — Range of Outcomes
   - Cup / O'Reilly / Trucks support
   - DK / FD / Both toggle
   - 2x through 7x value hit rates
   - Good/Boom/Smash/Bust are purely salary-value outcomes
   - Uses point_distributions.json when populated
   - Falls back to Floor/Ceiling normal-approx bins when dists are empty
   - Same slate/dark theme style as NASCAR projections
\------------------------------------------------------------------- */
const BASE = import.meta?.env?.BASE_URL ?? "/";
const SERIES_CONFIG = {
  cup: {
    key: "cup",
    label: "Cup",
    title: "NASCAR Cup — Range of Outcomes",
    source: `${BASE}data/nascar/cup/latest/projections.json`,
    driverData: `${BASE}data/nascar/cup/latest/data.json`,
    pointDists: `${BASE}data/nascar/cup/latest/point_distributions.json`,
    numLogoBase: `${BASE}logos/nascar/cup`,
    csvPrefix: "cup_range_of_outcomes",
  },
  xfinity: {
    key: "xfinity",
    label: "O'Reilly",
    title: "NASCAR O'Reilly — Range of Outcomes",
    source: `${BASE}data/nascar/xfinity/latest/projections.json`,
    driverData: `${BASE}data/nascar/xfinity/latest/data.json`,
    pointDists: `${BASE}data/nascar/xfinity/latest/point_distributions.json`,
    numLogoBase: `${BASE}logos/nascar/xfinity`,
    csvPrefix: "xfinity_range_of_outcomes",
  },
  trucks: {
    key: "trucks",
    label: "Trucks",
    title: "NASCAR Trucks — Range of Outcomes",
    source: `${BASE}data/nascar/trucks/latest/projections.json`,
    driverData: `${BASE}data/nascar/trucks/latest/data.json`,
    pointDists: `${BASE}data/nascar/trucks/latest/point_distributions.json`,
    numLogoBase: `${BASE}logos/nascar/trucks`,
    csvPrefix: "trucks_range_of_outcomes",
  },
};
const LOGO_DK = `${BASE}logos/dk.png`;
const LOGO_FD = `${BASE}logos/fd.png`;
const VALUE_MULTS = [2, 3, 4, 5, 6, 7];
/* ----------------------------- hooks ----------------------------- */
function useJson(url) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let alive = true;
    setLoading(true);
    setErr("");
    fetch(`${url}?_=${Date.now()}`, { cache: "no-store" })
      .then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json();
      })
      .then((j) => {
        if (alive) setData(j);
      })
      .catch((e) => {
        if (alive) {
          setErr(String(e));
          setData(null);
        }
      })
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [url]);
  return { data, err, loading };
}
function useLastUpdated(mainUrl, metaUrl) {
  const [updatedAt, setUpdatedAt] = useState(null);
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const h = await fetch(mainUrl, { method: "HEAD", cache: "no-store" });
        const lm = h.headers.get("last-modified");
        if (alive && lm) {
          setUpdatedAt(new Date(lm));
          return;
        }
      } catch {}
      try {
        const m = await fetch(`${metaUrl}?_=${Date.now()}`, { cache: "no-store" }).then((x) => x.json());
        const raw =
          m?.updated_iso ||
          m?.updated_utc ||
          m?.updated ||
          m?.last_updated ||
          m?.lastUpdated ||
          m?.timestamp ||
          null;
        const ep = m?.updated_epoch;
        const d = raw ? new Date(raw) : Number.isFinite(ep) ? new Date(ep * 1000) : null;
        if (alive && d && !isNaN(d)) setUpdatedAt(d);
      } catch {}
    })();
    return () => {
      alive = false;
    };
  }, [mainUrl, metaUrl]);
  return updatedAt;
}
/* ---------------------------- helpers ---------------------------- */
const num = (v) => {
  if (v == null || v === "") return null;
  const n = Number(String(v).replace(/[,$%\s]/g, ""));
  return Number.isFinite(n) ? n : null;
};
const fmt0 = (v) => {
  const n = num(v);
  return n == null ? "" : n.toLocaleString(undefined, { maximumFractionDigits: 0 });
};
const fmt1 = (v) => {
  const n = num(v);
  return n == null ? "" : n.toFixed(1);
};
const fmtPct = (v, d = 1) => {
  const n = num(v);
  return n == null ? "" : `${n.toFixed(d)}%`;
};
const fmtUpdated = (d) =>
  d
    ? d.toLocaleString(undefined, {
        month: "numeric",
        day: "numeric",
        hour: "numeric",
        minute: "2-digit",
      })
    : "—";
const keynorm = (s) =>
  String(s ?? "")
    .toLowerCase()
    .replace(/\./g, "")
    .replace(/\b(jr|sr|ii|iii|iv)\b/g, "$1")
    .replace(/[^a-z0-9\s]/g, "")
    .replace(/\s+/g, " ")
    .trim();
const pick = (row, keys, fallback = "") => {
  for (const k of keys) {
    if (row && Object.prototype.hasOwnProperty.call(row, k)) {
      const v = row[k];
      if (v !== "" && v != null) return v;
    }
  }
  return fallback;
};
function findDriverKey(row) {
  return Object.keys(row || {}).find((k) => /^driver\b/i.test(k)) || "Driver";
}
function findStartCol(row) {
  const keys = Object.keys(row || {});
  return (
    keys.find((k) => /^qual$/i.test(String(k).trim())) ||
    keys.find((k) => /^(start|starting)\s*(pos|position)$/i.test(String(k).trim())) ||
    keys.find((k) => /^grid$/i.test(String(k).trim())) ||
    keys.find((k) => /^starting\s*spot$/i.test(String(k).trim())) ||
    null
  );
}
function getRows(raw) {
  if (Array.isArray(raw)) return raw;
  if (Array.isArray(raw?.rows)) return raw.rows;
  if (Array.isArray(raw?.data)) return raw.data;
  return [];
}
function escapeCSV(s) {
  const v = String(s ?? "");
  return /[",\r\n]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v;
}
function downloadCSV(rows, columns, fname) {
  const header = columns.map((c) => c.label).join(",");
  const body = rows
    .map((r) =>
      columns
        .map((c) => {
          const v = r[c.key];
          if (c.type === "money") return escapeCSV(fmt0(v));
          if (c.type === "num1") return escapeCSV(fmt1(v));
          if (c.type === "pct") return escapeCSV(fmtPct(v));
          return escapeCSV(v ?? "");
        })
        .join(",")
    )
    .join("\n");
  const blob = new Blob([header + "\n" + body], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = fname;
  a.click();
  URL.revokeObjectURL(url);
}
/* ----------------------- distribution math ----------------------- */
function erf(x) {
  const sign = x < 0 ? -1 : 1;
  const a1 = 0.254829592;
  const a2 = -0.284496736;
  const a3 = 1.421413741;
  const a4 = -1.453152027;
  const a5 = 1.061405429;
  const p = 0.3275911;
  const ax = Math.abs(x);
  const t = 1 / (1 + p * ax);
  const y =
    1 -
    (((((a5 * t + a4) * t + a3) * t + a2) * t + a1) * t) * Math.exp(-ax * ax);
  return sign * y;
}
function normalCDF(x, mean, std) {
  if (!std || std <= 0) return x < mean ? 0 : 1;
  return 0.5 * (1 + erf((x - mean) / (std * Math.SQRT2)));
}
function niceWidth(w) {
  const cands = [2, 3, 4, 5, 6, 8, 10, 12, 15];
  let best = cands[0];
  let diff = Infinity;
  for (const c of cands) {
    const d = Math.abs(c - w);
    if (d < diff) {
      best = c;
      diff = d;
    }
  }
  return best;
}
function buildFallbackBins(proj, floor, ceiling) {
  const m = num(proj);
  const f = num(floor);
  const c = num(ceiling);
  if (m == null || f == null || c == null || c <= f) return [];
  const std = Math.max(1, (c - f) / 4);
  const minX = Math.floor(Math.min(0, f - std * 1.25));
  const maxX = Math.ceil(c + std * 1.5);
  const span = Math.max(1, maxX - minX);
  const width = niceWidth(Math.max(2, Math.round(span / 24)));
  const bins = [];
  for (let a = minX; a < maxX; a += width) {
    const b = Math.min(a + width, maxX);
    const p = normalCDF(b, m, std) - normalCDF(a, m, std);
    bins.push({ bin_start: a, bin_end: b, prob: Math.max(0, p) });
  }
  const sum = bins.reduce((acc, b) => acc + (Number(b.prob) || 0), 0);
  if (sum > 0) bins.forEach((b) => (b.prob = b.prob / sum));
  return bins;
}
function probAtLeast(bins, target) {
  const t = num(target);
  if (!Array.isArray(bins) || !bins.length || t == null) return null;
  let p = 0;
  for (const b of bins) {
    const a = Number(b?.bin_start);
    const z = Number(b?.bin_end);
    const bp = Number(b?.prob);
    if (!Number.isFinite(a) || !Number.isFinite(z) || !Number.isFinite(bp) || z <= a) continue;
    if (a >= t) p += bp;
    else if (z > t) {
      const share = (z - t) / (z - a);
      p += bp * Math.max(0, Math.min(1, share));
    }
  }
  return p * 100;
}
function probAtMost(bins, target) {
  const t = num(target);
  if (!Array.isArray(bins) || !bins.length || t == null) return null;
  let p = 0;
  for (const b of bins) {
    const a = Number(b?.bin_start);
    const z = Number(b?.bin_end);
    const bp = Number(b?.prob);
    if (!Number.isFinite(a) || !Number.isFinite(z) || !Number.isFinite(bp) || z <= a) continue;
    if (z <= t) p += bp;
    else if (a < t) {
      const share = (t - a) / (z - a);
      p += bp * Math.max(0, Math.min(1, share));
    }
  }
  return p * 100;
}
function buildDistIndex(raw) {
  const root = raw && typeof raw === "object" ? raw : null;
  const out = { dk: new Map(), fd: new Map() };
  if (!root) return out;
  const addSite = (siteKey, obj) => {
    if (!obj || typeof obj !== "object") return;
    for (const [rawKey, entry] of Object.entries(obj)) {
      const parts = String(rawKey).split("|").map((x) => x.trim());
      const driver = entry?.driver || entry?.Driver || entry?.player || entry?.Player || parts[0] || rawKey;
      const bins = Array.isArray(entry) ? entry : Array.isArray(entry?.bins) ? entry.bins : null;
      if (!bins || !bins.length) continue;
      const keys = [rawKey, driver, parts[0], entry?.name, entry?.Name]
        .filter(Boolean)
        .map(keynorm);
      for (const k of keys) {
        if (k && !out[siteKey].has(k)) out[siteKey].set(k, bins);
      }
    }
  };
  addSite("dk", root.dk || root.DK || {});
  addSite("fd", root.fd || root.FD || {});
  return out;
}
const OUTCOME_RULES = {
  dk: {
    // Pure salary-value thresholds.
    // No projection component and no raw-point minimums.
    good: { label: "Good", x: 4.5 },
    boom: { label: "Boom", x: 5.5 },
    smash: { label: "Smash", x: 6.5 },
  },
  fd: {
    // FanDuel uses a higher points-per-$1k scale.
    good: { label: "Good", x: 5.0 },
    boom: { label: "Boom", x: 6.0 },
    smash: { label: "Smash", x: 7.0 },
  },
};
function targetFor(salary, mult) {
  const s = num(salary);
  if (s == null || s <= 0) return null;
  return (s / 1000) * mult;
}
// Imported percentage fields are already percentage points.
// Examples: 0.8 = 0.8%, 15 = 15%. Do not multiply decimal-looking values by 100.
function normalizePercent(v) {
  return num(v);
}
function getOutcomeTargets(siteKey, salary) {
  const site = siteKey === "fd" ? "fd" : "dk";
  const rules = OUTCOME_RULES[site];
  const goodTarget = targetFor(salary, rules.good.x);
  const boomTarget = targetFor(salary, rules.boom.x);
  const smashTarget = targetFor(salary, rules.smash.x);
  // Bust is the exact complement of reaching the usable-value threshold.
  const bustTarget = goodTarget;
  return {
    goodTarget,
    boomTarget,
    smashTarget,
    bustTarget,
    goodLabel: `Good: >= ${rules.good.x}x salary`,
    boomLabel: `Boom: >= ${rules.boom.x}x salary`,
    smashLabel: `Smash: >= ${rules.smash.x}x salary`,
    bustLabel: `Bust: < ${rules.good.x}x salary`,
  };
}
function rangeScore(row) {
  const good = Math.max(0, num(row.good) ?? 0);
  const boom = Math.max(0, num(row.boom) ?? 0);
  const smash = Math.max(0, num(row.smash) ?? 0);
  const opt = Math.max(0, num(row.opt) ?? 0);
  const lev = num(row.lev) ?? 0;
  // Good/Boom/Smash are nested probabilities. Convert them into exclusive buckets
  // so a Smash outcome is not counted three separate times.
  const smashOnly = smash;
  const boomOnly = Math.max(0, boom - smash);
  const goodOnly = Math.max(0, good - boom);
  // Outcome quality is the main score. Optimal rate and positive leverage are only
  // small secondary adjustments.
  const outcomeQuality =
    goodOnly * 0.45 +
    boomOnly * 0.75 +
    smashOnly * 1.00;
  const positiveLev = Math.max(0, lev);
  const negativeLev = Math.max(0, -lev);
  const score =
    outcomeQuality * 0.85 +
    opt * 0.10 +
    positiveLev * 0.05 -
    negativeLev * 0.03;
  return Math.max(0, Math.min(100, score));
}
/* -------------------------- heat coloring ------------------------- */
function dirForKey(key) {
  if (["sal", "bust", "pown", "start"].includes(key)) return "lower";
  if (
    [
      "proj",
      "val",
      "p2x",
      "p3x",
      "p4x",
      "p5x",
      "p6x",
      "p7x",
      "good",
      "boom",
      "smash",
      "score",
      "opt",
      "lev",
      "rtg",
      "pll",
      "pfl",
      "dom",
    ].includes(key)
  )
    return "higher";
  return null;
}
function heatTextColor(min, max, v, dir, theme = "dark") {
  if (!Number.isFinite(min) || !Number.isFinite(max) || !Number.isFinite(v) || min === max || !dir) return null;
  let t = (v - min) / (max - min);
  t = Math.max(0, Math.min(1, t));
  if (dir === "lower") t = 1 - t;
  const dark = theme === "dark";
  if (t < 0.5) {
    const u = t / 0.5;
    const h = 8 + u * 42;
    return dark ? `hsl(${h}, 90%, ${70 - u * 8}%)` : `hsl(${h}, 85%, ${48 - u * 4}%)`;
  }
  const u = (t - 0.5) / 0.5;
  const h = 50 + u * 70;
  return dark ? `hsl(${h}, 88%, ${62 + u * 8}%)` : `hsl(${h}, 80%, ${44 - u * 4}%)`;
}
/* --------------------------- modal --------------------------- */
function PointDistModal({ open, onClose, isDark, site, driver, bins, proj }) {
  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" ? window.innerWidth < 640 : false
  );
  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const handleResize = () => setIsMobile(window.innerWidth < 640);
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);
  const chartData = Array.isArray(bins)
    ? bins.map((b, i) => {
        const a = Number(b?.bin_start);
        const z = Number(b?.bin_end);
        const p = Number(b?.prob);
        return {
          label: Number.isFinite(a) && Number.isFinite(z) ? `${a}-${z}` : `bin-${i + 1}`,
          shortLabel: Number.isFinite(a) ? `${a}` : `bin-${i + 1}`,
          bin_start: a,
          bin_end: z,
          mid: Number.isFinite(a) && Number.isFinite(z) ? (a + z) / 2 : i,
          probPct: Number.isFinite(p) ? p * 100 : 0,
        };
      })
    : [];
  const projX = Number.isFinite(proj) ? proj : null;
  const xDomain = useMemo(() => {
    const mids = chartData
      .map((d) => d.mid)
      .filter(Number.isFinite)
      .sort((a, b) => a - b);
    if (!mids.length) return [0, 1];
    if (mids.length === 1) return [mids[0] - 2.5, mids[0] + 2.5];
    const firstGap = Math.abs(mids[1] - mids[0]) || 1;
    const lastGap =
      Math.abs(mids[mids.length - 1] - mids[mids.length - 2]) || firstGap;
    return [
      mids[0] - firstGap / 2,
      mids[mids.length - 1] + lastGap / 2,
    ];
  }, [chartData]);
  const xTicks = useMemo(() => {
    if (!chartData.length) return [];
    return chartData
      .filter((_, i) => (isMobile ? i % 4 === 0 : i % 2 === 0))
      .map((d) => d.mid)
      .filter(Number.isFinite);
  }, [chartData, isMobile]);
  if (!open) return null;
  return (
    <div
      className="fixed inset-0 z-[200] flex items-center justify-center p-2 sm:p-4"
      role="dialog"
      aria-modal="true"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose?.();
      }}
    >
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />
      <div
        className={[
          "relative w-full max-w-3xl max-h-[95vh] rounded-2xl border shadow-2xl overflow-y-auto",
          isDark ? "border-slate-700/60 bg-slate-950 text-slate-100" : "border-slate-200 bg-white text-slate-900",
        ].join(" ")}
      >
        <div className={isDark ? "bg-slate-950/95" : "bg-white"}>
          <div className="flex items-start justify-between gap-3 px-3 pt-4 sm:gap-4 sm:px-5 sm:pt-5">
            <div className="min-w-0">
              <div className={isDark ? "text-xs text-slate-400" : "text-xs text-slate-500"}>Fantasy Points Distribution</div>
              <div className="mt-1 flex flex-wrap items-center gap-2">
                <div className="max-w-[190px] truncate text-base font-extrabold tracking-tight sm:max-w-none sm:text-lg">{driver || "—"}</div>
                <span
                  className={[
                    "inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-bold tracking-wide border",
                    site === "DK"
                      ? isDark
                        ? "bg-emerald-500/15 text-emerald-200 border-emerald-500/30"
                        : "bg-emerald-50 text-emerald-800 border-emerald-200"
                      : isDark
                      ? "bg-sky-500/15 text-sky-200 border-sky-500/30"
                      : "bg-sky-50 text-sky-800 border-sky-200",
                  ].join(" ")}
                >
                  {site}
                </span>
                {Number.isFinite(proj) ? (
                  <span className={isDark ? "text-xs text-slate-300" : "text-xs text-slate-600"}>
                    Proj: <span className="font-semibold tabular-nums">{proj.toFixed(1)}</span>
                  </span>
                ) : null}
              </div>
            </div>
            <button
              className={[
                "shrink-0 rounded-xl border px-2.5 py-1.5 text-xs font-semibold sm:px-3 sm:py-2 sm:text-sm",
                isDark ? "border-slate-700 bg-slate-900/40 hover:bg-slate-900" : "border-slate-200 bg-slate-50 hover:bg-slate-100",
              ].join(" ")}
              onClick={onClose}
            >
              Close
            </button>
          </div>
          <div className="px-2 pb-4 pt-3 sm:px-5 sm:pb-5 sm:pt-4">
            {!chartData.length ? (
              <div className={isDark ? "text-sm text-slate-300" : "text-sm text-slate-700"}>No distribution data found.</div>
            ) : (
              <div className={isMobile ? "h-[360px] w-full" : "h-[320px] w-full"}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={chartData}
                    margin={{
                      top: 10,
                      right: 18,
                      left: 8,
                      bottom: isMobile ? 42 : 18,
                    }}
                    barCategoryGap={isMobile ? "12%" : "8%"}
                  >
                    <CartesianGrid stroke={isDark ? "#334155" : "#e5e7eb"} strokeDasharray="3 3" />
                    <XAxis
                      type="number"
                      dataKey="mid"
                      domain={xDomain}
                      ticks={xTicks}
                      tickFormatter={(v) => {
                        const hit = chartData.find((d) => d.mid === v);
                        return hit?.shortLabel ?? `${Math.round(v)}`;
                      }}
                      height={isMobile ? 52 : 30}
                      angle={isMobile ? -35 : 0}
                      textAnchor={isMobile ? "end" : "middle"}
                      tickMargin={6}
                      tick={{
                        fontSize: isMobile ? 11 : 12,
                        fill: isDark ? "#cbd5f5" : "#334155",
                      }}
                      axisLine={{ stroke: isDark ? "#475569" : "#cbd5e1" }}
                      tickLine={false}
                    />
                    <YAxis
                      tickFormatter={(v) => `${v}%`}
                      tick={{ fontSize: isMobile ? 10 : 11, fill: isDark ? "#cbd5f5" : "#334155" }}
                      axisLine={{ stroke: isDark ? "#475569" : "#cbd5e1" }}
                      tickLine={false}
                      width={isMobile ? 38 : 60}
                    />
                    <Tooltip
                      cursor={{ fill: "rgba(56,189,248,0.12)" }}
                      contentStyle={{
                        backgroundColor: isDark ? "#020617" : "#ffffff",
                        border: "1px solid #38bdf8",
                        borderRadius: 10,
                        fontSize: 12,
                      }}
                      labelStyle={{ color: isDark ? "#e2e8f0" : "#0f172a" }}
                      formatter={(v) => [`${Number(v).toFixed(2)}%`, "Probability"]}
                      labelFormatter={(l) => `Bin ${l}`}
                    />
                    {Number.isFinite(projX) ? (
                      <ReferenceLine x={projX} stroke="#38bdf8" strokeWidth={2} strokeDasharray="6 4" />
                    ) : null}
                    <Bar dataKey="probPct" radius={[6, 6, 0, 0]} fill="#7dd3fc" stroke="#38bdf8" strokeWidth={1} maxBarSize={42} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
/* --------------------------- page --------------------------- */
export default function NascarRangeOfOutcomes({ series = "cup" }) {
  const initialSeries = SERIES_CONFIG[String(series || "cup").toLowerCase()] ? String(series || "cup").toLowerCase() : "cup";
  const [seriesKey, setSeriesKey] = useState(initialSeries);
  const cfg = SERIES_CONFIG[seriesKey] || SERIES_CONFIG.cup;
  const META = cfg.source.replace(/projections\.json$/, "meta.json");
  const { data: projectionsRaw, err: projErr, loading: projLoading } = useJson(cfg.source);
  const { data: driverDataRaw } = useJson(cfg.driverData);
  const { data: pointDistsRaw } = useJson(cfg.pointDists);
  const updatedAt = useLastUpdated(cfg.source, META);
  const [theme, setTheme] = useState("dark");
  const [site, setSite] = useState("dk"); // dk | fd | both
  const [q, setQ] = useState("");
  const [selectedTeam, setSelectedTeam] = useState("");
  const [selectedManufacturer, setSelectedManufacturer] = useState("");
  const [sort, setSort] = useState({ key: "score", dir: "desc" });
  const [modal, setModal] = useState({ open: false, site: "DK", driver: "", bins: [], proj: null });
  const isDark = theme === "dark";
  const rowsRaw = useMemo(() => getRows(projectionsRaw), [projectionsRaw]);
  const dataRows = useMemo(() => getRows(driverDataRaw), [driverDataRaw]);
  const driverKey = useMemo(() => findDriverKey(rowsRaw[0] || {}), [rowsRaw]);
  const startCol = useMemo(() => findStartCol(rowsRaw[0] || {}), [rowsRaw]);
  const distIndex = useMemo(() => buildDistIndex(pointDistsRaw), [pointDistsRaw]);
  const driverDetails = useMemo(() => {
    const details = new Map();
    for (const r of dataRows) {
      const name = pick(r, ["Driver", "Driver Name", "Name"]);
      if (!name) continue;
      details.set(keynorm(name), {
        team: String(pick(r, ["Team", "Race Team"])).trim(),
        manufacturer: String(pick(r, ["Car Make", "Make", "Manufacturer"])).trim(),
      });
    }
    return details;
  }, [dataRows]);
  const driverNumMap = useMemo(() => {
    const m = new Map();
    if (!dataRows.length) return m;
    const nameCol = Object.keys(dataRows[0] || {}).find((k) => /^driver\b|^name$/i.test(k)) || "Driver";
    const numCol =
      Object.keys(dataRows[0] || {}).find((k) => /^car\s*#$/i.test(k)) ||
      Object.keys(dataRows[0] || {}).find((k) => /^car#$/i.test(k)) ||
      Object.keys(dataRows[0] || {}).find((k) => /^number$/i.test(k)) ||
      "Car #";
    for (const r of dataRows) {
      const k = keynorm(r?.[nameCol]);
      const n = num(r?.[numCol]);
      if (k && n != null) m.set(k, Math.round(n));
    }
    return m;
  }, [dataRows]);
  const normalizedRows = useMemo(() => {
    return rowsRaw.map((r, idx) => {
      const driver = String(r?.[driverKey] ?? r?.Driver ?? r?.driver ?? "").trim();
      const k = keynorm(driver);
      const details = driverDetails.get(k) || {};
      const carNum = driverNumMap.get(k) ?? num(pick(r, ["Car #", "Car#", "Number", "#"])) ?? null;
      const start = startCol ? pick(r, [startCol]) : pick(r, ["Qual", "Start", "Starting Pos", "Grid"]);
      const dk = {
        sal: pick(r, ["DK Sal", "DK Salary", "dk_sal", "DK"]),
        proj: pick(r, ["DK Proj", "DK Projection", "dk_proj"]),
        val: pick(r, ["DK Val", "DKV", "dk_val"]),
        floor: pick(r, ["DK Floor", "dk_floor"]),
        ceiling: pick(r, ["DK Ceiling", "DK Ceil", "dk_ceiling"]),
        pown: pick(r, ["DK pOWN%", "DK pOWN", "DK Own", "dk_pown"]),
        opt: pick(r, ["DK Opt%", "DK Opt", "dk_opt"]),
        lev: pick(r, ["DK Lev%", "DK Lev", "dk_lev"]),
        rtg: pick(r, ["DK Rtg", "DK RTG", "Rating"]),
        dom: pick(r, ["DK Dom", "dk_dom"]),
      };
      const fd = {
        sal: pick(r, ["FD Sal", "FD Salary", "fd_sal", "FD"]),
        proj: pick(r, ["FD Proj", "FD Projection", "fd_proj"]),
        val: pick(r, ["FD Val", "FDV", "fd_val"]),
        floor: pick(r, ["FD Floor", "fd_floor"]),
        ceiling: pick(r, ["FD Ceiling", "FD Ceil", "fd_ceiling"]),
        pown: pick(r, ["FD pOWN%", "FD pOWN", "FD Own", "fd_pown"]),
        opt: pick(r, ["FD Opt%", "FD Opt", "fd_opt"]),
        lev: pick(r, ["FD Lev%", "FD Lev", "fd_lev"]),
        rtg: pick(r, ["FD RTG", "FD Rtg", "Rating"]),
        dom: pick(r, ["FD Dom", "fd_dom"]),
      };
      const common = {
        id: `${driver}-${idx}`,
        driver,
        key: k,
        team: details.team || String(pick(r, ["Team", "Race Team"])).trim(),
        manufacturer: details.manufacturer || String(pick(r, ["Car Make", "Make", "Manufacturer"])).trim(),
        carNum,
        start,
        pll: pick(r, ["pLL", "PLL", "Pll", "ll", "p_ll"]),
        pfl: pick(r, ["pFL", "PFL", "Pfl", "fl", "p_fl"]),
        raw: r,
      };
      return { ...common, dk, fd };
    });
  }, [rowsRaw, driverKey, startCol, driverNumMap, driverDetails]);
  const teamOptions = useMemo(() =>
    [...new Set(normalizedRows.map((r) => r.team).filter(Boolean))].sort((a, b) => a.localeCompare(b)),
    [normalizedRows]);
  const manufacturerOptions = useMemo(() =>
    [...new Set(normalizedRows.map((r) => r.manufacturer).filter(Boolean))].sort((a, b) => a.localeCompare(b)),
    [normalizedRows]);
  useEffect(() => {
    setQ("");
    setSelectedTeam("");
    setSelectedManufacturer("");
  }, [seriesKey]);
  const siteRows = useMemo(() => {
    const makeOne = (base, siteKey) => {
      const s = base[siteKey];
      const siteLabel = siteKey.toUpperCase();
      const sal = num(s.sal);
      const proj = num(s.proj);
      const floor = num(s.floor);
      const ceiling = num(s.ceiling);
      const distFromFile = distIndex[siteKey]?.get(base.key) || null;
      const bins = distFromFile || buildFallbackBins(proj, floor, ceiling);
      const distSource = distFromFile ? "File" : bins.length ? "Fallback" : "None";
      const pcts = {};
      for (const mult of VALUE_MULTS) {
        const target = targetFor(sal, mult);
        pcts[`p${String(mult).replace(".", "_")}x`] = target == null ? null : probAtLeast(bins, target);
      }
      const targets = getOutcomeTargets(siteKey, sal);
      const good = targets.goodTarget == null ? null : probAtLeast(bins, targets.goodTarget);
      const boom = targets.boomTarget == null ? null : probAtLeast(bins, targets.boomTarget);
      const smash = targets.smashTarget == null ? null : probAtLeast(bins, targets.smashTarget);
      const bust = targets.bustTarget == null ? null : probAtMost(bins, targets.bustTarget);
      const out = {
        id: `${base.id}-${siteKey}`,
        site: siteLabel,
        siteKey,
        driver: base.driver,
        key: base.key,
        team: base.team,
        manufacturer: base.manufacturer,
        carNum: base.carNum,
        start: base.start,
        sal,
        proj,
        val: num(s.val) ?? (sal && proj != null ? proj / (sal / 1000) : null),
        floor,
        ceiling,
        pown: normalizePercent(s.pown),
        opt: normalizePercent(s.opt),
        // Derive leverage consistently as Optimal% minus projected Ownership%.
        // Percent fields are already stored as percentage points (0.8 = 0.8%).
        lev:
          normalizePercent(s.opt) != null && normalizePercent(s.pown) != null
            ? normalizePercent(s.opt) - normalizePercent(s.pown)
            : null,
        rtg: num(s.rtg),
        dom: num(s.dom),
        pll: num(base.pll),
        pfl: num(base.pfl),
        p2x: pcts.p2x,
        p3x: pcts.p3x,
        p4x: pcts.p4x,
        p5x: pcts.p5x,
        p6x: pcts.p6x,
        p7x: pcts.p7x,
        good,
        boom,
        smash,
        bust,
        goodTarget: targets.goodTarget,
        boomTarget: targets.boomTarget,
        smashTarget: targets.smashTarget,
        bustTarget: targets.bustTarget,
        goodLabel: targets.goodLabel,
        boomLabel: targets.boomLabel,
        smashLabel: targets.smashLabel,
        bustLabel: targets.bustLabel,
        bins,
        distSource,
      };
      out.score = rangeScore(out);
      return out;
    };
    let arr = [];
    for (const r of normalizedRows) {
      if (site === "dk" || site === "both") arr.push(makeOne(r, "dk"));
      if (site === "fd" || site === "both") arr.push(makeOne(r, "fd"));
    }
    return arr.filter((r) => r.driver && (num(r.sal) || num(r.proj) || r.bins.length));
  }, [normalizedRows, site, distIndex]);
  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    return siteRows.filter((r) => {
      if (selectedTeam && r.team !== selectedTeam) return false;
      if (selectedManufacturer && r.manufacturer !== selectedManufacturer) return false;
      return !s || `${r.driver} ${r.carNum ?? ""} ${r.site} ${r.team} ${r.manufacturer}`.toLowerCase().includes(s);
    });
  }, [siteRows, q, selectedTeam, selectedManufacturer]);
  const columns = useMemo(() => {
    const showSite = site === "both";
    return [
      { key: "driver", label: "Driver", type: "text", sticky: true, cls: "w-[210px] max-w-[210px]" },
      ...(showSite ? [{ key: "site", label: "Site", type: "text", cls: "w-[56px]" }] : []),
      { key: "start", label: "Start", type: "num1", cls: "w-[64px]" },
      { key: "sal", label: "Salary", type: "money", cls: "w-[86px]" },
      { key: "proj", label: "Proj", type: "num1", cls: "w-[74px]", clickable: true },
      { key: "val", label: "Val", type: "num1", cls: "w-[64px]" },
      { key: "p2x", label: "2x", type: "pct", cls: "w-[66px]" },
      { key: "p3x", label: "3x", type: "pct", cls: "w-[66px]" },
      { key: "p4x", label: "4x", type: "pct", cls: "w-[66px]" },
      { key: "p5x", label: "5x", type: "pct", cls: "w-[66px]" },
      { key: "p6x", label: "6x", type: "pct", cls: "w-[66px]" },
      { key: "p7x", label: "7x", type: "pct", cls: "w-[66px]" },
      { key: "good", label: "Good", type: "pct", cls: "w-[76px]" },
      { key: "boom", label: "Boom", type: "pct", cls: "w-[76px]" },
      { key: "smash", label: "Smash", type: "pct", cls: "w-[76px]" },
      { key: "bust", label: "Bust", type: "pct", cls: "w-[76px]" },
      { key: "score", label: "ROO Score", type: "num1", cls: "w-[88px]" },
      { key: "pown", label: "pOWN%", type: "pct", cls: "w-[76px]" },
      { key: "opt", label: "Opt%", type: "pct", cls: "w-[72px]" },
      { key: "lev", label: "Lev%", type: "pct", cls: "w-[72px]" },
      { key: "pll", label: "LL", type: "num1", cls: "w-[66px]" },
      { key: "pfl", label: "FL", type: "num1", cls: "w-[66px]" },
      { key: "dom", label: "Dom", type: "num1", cls: "w-[66px]" },
    ];
  }, [site]);
  const heatStats = useMemo(() => {
    const stats = {};
    for (const c of columns) {
      const dir = dirForKey(c.key);
      if (!dir) continue;
      let min = Infinity;
      let max = -Infinity;
      for (const r of filtered) {
        const v = num(r[c.key]);
        if (v == null) continue;
        if (v < min) min = v;
        if (v > max) max = v;
      }
      if (min !== Infinity && max !== -Infinity) stats[c.key] = { min, max, dir };
    }
    return stats;
  }, [filtered, columns]);
  const sorted = useMemo(() => {
    const arr = [...filtered];
    const dir = sort.dir === "asc" ? 1 : -1;
    const col = columns.find((c) => c.key === sort.key);
    arr.sort((a, b) => {
      if (col?.type === "text") {
        const av = String(a[sort.key] ?? "").toLowerCase();
        const bv = String(b[sort.key] ?? "").toLowerCase();
        return (av < bv ? -1 : av > bv ? 1 : 0) * dir;
      }
      const av = num(a[sort.key]);
      const bv = num(b[sort.key]);
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      return (av - bv) * dir;
    });
    return arr;
  }, [filtered, sort, columns]);
  const onSort = (key) => {
    setSort((p) => (p.key === key ? { key, dir: p.dir === "desc" ? "asc" : "desc" } : { key, dir: "desc" }));
  };
  const openDist = (r) => {
    setModal({
      open: true,
      site: r.site,
      driver: r.driver,
      bins: r.bins,
      proj: Number.isFinite(r.proj) ? r.proj : null,
    });
  };
  const outerClass = isDark ? "min-h-screen bg-slate-950 text-slate-100" : "min-h-screen bg-slate-100 text-slate-900";
  const cardClass = isDark
    ? "rounded-2xl border border-slate-800 bg-slate-900/95 shadow-sm"
    : "rounded-2xl border border-slate-200 bg-white shadow-sm";
  const headerBg = isDark ? "bg-slate-900 text-slate-200" : "bg-slate-50 text-slate-700";
  const headerCell = isDark ? "bg-slate-900" : "bg-slate-50";
  const rowOdd = isDark ? "bg-slate-950" : "bg-white";
  const rowEven = isDark ? "bg-slate-900" : "bg-gray-50";
  const loading = projLoading;
  const err = projErr;
  return (
    <div className={outerClass}>
      <PointDistModal
        open={modal.open}
        onClose={() => setModal((m) => ({ ...m, open: false }))}
        isDark={isDark}
        site={modal.site}
        driver={modal.driver}
        bins={modal.bins}
        proj={modal.proj}
      />
      <div className="w-full px-4 md:px-6 py-5">
        <header className="mb-4 space-y-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex min-w-0 flex-wrap items-baseline gap-x-3 gap-y-1">
              <h1 className="text-xl font-bold tracking-tight">{cfg.title}</h1>
              <span className={"text-xs font-normal italic " + (isDark ? "text-slate-100" : "text-slate-700")}>Updated: {fmtUpdated(updatedAt)}</span>
            </div>
            <div className="flex shrink-0 items-center gap-3">
              <div role="group" aria-label="Color theme" className={"inline-flex items-center rounded-full px-1 py-1 text-[11px] ring-1 " +
                (isDark ? "bg-slate-900/90 ring-slate-700/80" : "bg-white ring-slate-300")}>
                <button type="button" aria-pressed={isDark} onClick={() => setTheme("dark")}
                  className={"rounded-full px-3 py-1 font-medium transition " +
                    (isDark ? "bg-sky-500 text-slate-950 shadow-sm" : "text-slate-500 hover:text-slate-800")}>Dark</button>
                <button type="button" aria-pressed={!isDark} onClick={() => setTheme("light")}
                  className={"rounded-full px-3 py-1 font-medium transition " +
                    (!isDark ? "bg-sky-500 text-slate-950 shadow-sm" : "text-slate-400 hover:text-slate-100")}>Light</button>
              </div>
            </div>
          </div>
          <div className={cardClass + " px-4 py-3"}>
            <nav aria-label="NASCAR series" className={"inline-flex flex-wrap items-center gap-5 border-b " + (isDark ? "border-slate-800" : "border-slate-200")}>
              {Object.values(SERIES_CONFIG).map((s) => (
                <button
                  key={s.key}
                  type="button"
                  aria-current={seriesKey === s.key ? "page" : undefined}
                  onClick={() => {
                    setSeriesKey(s.key);
                    setModal((m) => ({ ...m, open: false }));
                  }}
                  className={"relative inline-flex h-9 items-center whitespace-nowrap pb-2 text-xs font-semibold transition-colors " +
                    (seriesKey === s.key
                      ? (isDark ? "text-white" : "text-slate-950") + " after:absolute after:bottom-[-1px] after:left-0 after:right-0 after:h-0.5 after:rounded-full after:bg-sky-400"
                      : isDark ? "text-slate-400 hover:text-slate-100" : "text-slate-500 hover:text-slate-900")}
                >{s.label}</button>
              ))}
            </nav>
          </div>
        </header>
        <section aria-label="Driver outcomes" className={cardClass}>
          <div className={"border-b px-4 py-3 text-xs font-semibold " + (isDark ? "border-slate-800" : "border-slate-200")}>Driver Outcomes <span className={"ml-2 text-[11px] font-normal " + (isDark ? "text-slate-400" : "text-slate-500")}>2x–7x salary-tag hit rates</span></div>
          <div className={"flex flex-wrap items-center gap-2 border-b px-3 py-3 md:px-4  " +
            (isDark ? "border-slate-800/80" : "border-slate-200/80")}>
            <input
              type="search"
              aria-label="Search driver, car number, team, or manufacturer"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search driver / team / make…"
              className={"h-8 w-full min-w-0 rounded-md border px-4 text-xs focus:outline-none focus:ring-2 focus:ring-sky-500 sm:flex-[1_1_200px] xl:flex-[1_1_180px] " +
                (isDark ? "border-slate-700 bg-slate-950/80 text-slate-100 placeholder:text-slate-500" : "border-slate-300 bg-white text-slate-900 placeholder:text-slate-400")}
            />
            <select aria-label="Filter by team" value={selectedTeam} onChange={(e) => setSelectedTeam(e.target.value)}
              className={"h-8 min-w-0 w-[calc(50%-0.375rem)] flex-none rounded-md border px-3 text-xs sm:w-auto sm:flex-[0_1_180px] xl:w-[180px] xl:flex-none " +
                (isDark ? "border-slate-700 bg-slate-950 text-slate-100" : "border-slate-300 bg-white text-slate-900")}>
              <option value="">All teams</option>
              {teamOptions.map((team) => <option key={team} value={team}>{team}</option>)}
            </select>
            <select aria-label="Filter by manufacturer" value={selectedManufacturer} onChange={(e) => setSelectedManufacturer(e.target.value)}
              className={"h-8 min-w-0 w-[calc(50%-0.375rem)] flex-none rounded-md border px-3 text-xs sm:w-auto sm:flex-[0_1_175px] xl:w-[175px] xl:flex-none " +
                (isDark ? "border-slate-700 bg-slate-950 text-slate-100" : "border-slate-300 bg-white text-slate-900")}>
              <option value="">All manufacturers</option>
              {manufacturerOptions.map((make) => <option key={make} value={make}>{make}</option>)}
            </select>
            <div role="tablist" aria-label="Fantasy site" className={"inline-flex shrink-0 items-center gap-1 border-b " +
              (isDark ? "border-slate-800" : "border-slate-200")}>
              {[
                { key: "dk", label: "DK", logo: LOGO_DK },
                { key: "fd", label: "FD", logo: LOGO_FD },
                { key: "both", label: "Both" },
              ].map((s) => (
                <button key={s.key} type="button" role="tab" aria-selected={site === s.key}
                  onClick={() => { setSite(s.key); setModal((m) => ({ ...m, open: false })); }}
                  className={"inline-flex h-9 items-center gap-1.5 border-b-2 px-3 text-xs font-semibold transition " +
                    (site === s.key ? (isDark ? "border-sky-400 text-white" : "border-sky-500 text-slate-900") : isDark ? "border-transparent text-slate-400 hover:text-white" : "border-transparent text-slate-500 hover:text-slate-900")}>
                  {s.logo && <img src={s.logo} alt="" className="h-4 w-4" />}
                  {s.label}
                </button>
              ))}
            </div>
            <button type="button" onClick={() => { setQ(""); setSelectedTeam(""); setSelectedManufacturer(""); }}
              className={"h-8 shrink-0 rounded-md border px-4 text-xs transition " +
                (isDark ? "border-slate-700 bg-slate-950 text-slate-100 hover:bg-slate-900" : "border-slate-300 bg-white text-slate-900 hover:bg-slate-50")}>Reset</button>
            <button type="button" onClick={() => downloadCSV(sorted, columns, `${cfg.csvPrefix}.csv`)}
              className="h-8 shrink-0 rounded-md bg-sky-500 px-4 text-xs font-semibold text-slate-950 shadow-sm hover:bg-sky-400">Export CSV</button>
          </div>
          <details className={"border-b px-4 py-2 text-xs " + (isDark ? "border-slate-800/80 text-slate-400" : "border-slate-200 text-slate-600")}>
            <summary className="cursor-pointer font-medium">Value hit definitions</summary>
            <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1">
              <span>Good = salary value hit</span>
              <span>Boom = strong value hit</span>
              <span>Smash = elite value hit</span>
              <span>Click an underlined projection for its distribution.</span>
            </div>
          </details>
          <div className="overflow-auto max-h-[72vh] rounded-b-2xl">
            <table className="border-separate table-auto w-max min-w-full text-[11px] md:text-xs" style={{ borderSpacing: 0 }}>
              <thead className={`${headerBg} sticky top-0 z-20 shadow-[0_2px_0_0_rgba(0,0,0,0.35)]`}>
                <tr>
                  {columns.map((c, idx) => (
                    <th
                      key={c.key}
                      className={`px-2 py-1 font-semibold text-center whitespace-nowrap cursor-pointer select-none ${c.cls || ""} ${headerCell} ${idx === 0 ? "sticky left-0 z-30" : ""}`}
                      onClick={() => onSort(c.key)}
                    >
                      <div className="inline-flex items-center gap-1">
                        <span>{c.label}</span>
                        {sort.key === c.key ? <span className="text-[10px] text-slate-400">{sort.dir === "desc" ? "▼" : "▲"}</span> : <span className="text-[9px] text-slate-600">▲</span>}
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr>
                    <td className="px-3 py-4 text-center text-slate-400" colSpan={columns.length}>
                      Loading…
                    </td>
                  </tr>
                )}
                {err && !loading && (
                  <tr>
                    <td className="px-3 py-4 text-center text-red-400" colSpan={columns.length}>
                      Failed to load: {err}
                    </td>
                  </tr>
                )}
                {!loading &&
                  !err &&
                  sorted.map((r, i) => {
                    const rowBg = i % 2 === 0 ? rowOdd : rowEven;
                    const iconSrc = r.carNum != null ? `${cfg.numLogoBase}/${r.carNum}.png` : null;
                    return (
                      <tr key={r.id} className={rowBg}>
                        {columns.map((c, idx) => {
                          if (c.key === "driver") {
                            return (
                              <td key={c.key} className={`px-2 py-1 text-left sticky left-0 z-10 ${rowBg} shadow-[inset_-6px_0_8px_-6px_rgba(0,0,0,0.6)] w-[210px] max-w-[210px]`}>
                                <div className="flex items-center gap-2 min-w-0">
                                  {iconSrc ? (
                                    <img
                                      src={iconSrc}
                                      alt={r.carNum ?? ""}
                                      className="h-5 w-5 object-contain flex-none"
                                      onError={(e) => {
                                        e.currentTarget.style.display = "none";
                                      }}
                                    />
                                  ) : (
                                    <div className="h-5 w-5 rounded-full bg-slate-700/40 flex-none" />
                                  )}
                                  <span className="truncate font-semibold">{r.driver}</span>
                                </div>
                              </td>
                            );
                          }
                          let content = r[c.key];
                          if (c.type === "money") content = fmt0(content);
                          if (c.type === "num1") content = fmt1(content);
                          if (c.type === "pct") content = fmtPct(content);
                          const stat = heatStats[c.key];
                          const v = num(r[c.key]);
                          const color = stat && v != null ? heatTextColor(stat.min, stat.max, v, stat.dir, theme) : null;
                          if (c.key === "proj") {
                            return (
                              <td key={c.key} className={`${c.cls || ""} px-1.5 py-1 text-center whitespace-nowrap tabular-nums`}>
                                <button
                                  type="button"
                                  onClick={() => openDist(r)}
                                  className="font-bold hover:text-sky-300 focus:outline-none focus:ring-2 focus:ring-sky-500/60 rounded px-1"
                                  style={{
                                    color: color || undefined,
                                    textDecorationLine: "underline",
                                    textUnderlineOffset: "4px",
                                    textDecorationThickness: "1px",
                                  }}
                                  title="Open distribution"
                                >
                                  {content}
                                </button>
                              </td>
                            );
                          }
                          const extraTitle =
                            c.key === "good"
                              ? `${r.goodLabel} | target ${fmt1(r.goodTarget)} pts`
                              : c.key === "boom"
                              ? `${r.boomLabel} | target ${fmt1(r.boomTarget)} pts`
                              : c.key === "smash"
                              ? `${r.smashLabel} | target ${fmt1(r.smashTarget)} pts`
                              : c.key === "bust"
                              ? `${r.bustLabel} | target ${fmt1(r.bustTarget)} pts`
                              : String(content ?? "");
                          return (
                            <td
                              key={c.key}
                              className={`${c.cls || ""} px-1.5 py-1 text-center whitespace-nowrap ${c.type !== "text" ? "tabular-nums" : ""}`}
                              style={color ? { color, fontWeight: 700 } : undefined}
                              title={extraTitle}
                            >
                              {content}
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })}
                {!loading && !err && !sorted.length && (
                  <tr>
                    <td className="px-3 py-4 text-center text-slate-400" colSpan={columns.length}>
                      No data.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </section>
        <div className={isDark ? "mt-3 text-[11px] text-slate-500" : "mt-3 text-[11px] text-slate-600"}>
          NASCAR scale: outcomes are purely salary-value based. DK Good/Bust split at 4.5x, Boom at 5.5x, and Smash at 6.5x. FD uses 5x/6x/7x. Leverage is Opt% minus pOWN%. LL and FL are raw projected laps led and fastest laps.
        </div>
      </div>
    </div>
  );
}
